import { useEffect, useMemo, useState } from "react";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    Loader2,
    Sparkles
} from "lucide-react";
import toast from "react-hot-toast";

import {
    createAndQueuePreviewSession,
    getPreviewSession,
    getPreviewSessionStatus,
    claimPreviewSession
} from "../services/websiteService";

import {
    getCurrentUser,
    redirectToSignup,
    redirectToLogin
} from "../services/authService";

import "./MobileGeneratePage.css";

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "https://api.shrianshnexus.in";

const industryOptions = [
    "Fitness",
    "Education",
    "Real Estate",
    "Healthcare",
    "Restaurant",
    "Online Store",
    "Legal",
    "Beauty",
    "SaaS"
];

const ctaOptions = [
    "Book Trial",
    "Contact Us",
    "Get Quote",
    "View Services",
    "Join Program",
    "Buy Online"
];

const styleOptions = [
    "Premium",
    "Modern",
    "Clean",
    "Bold",
    "Luxury"
];

const loadingSteps = [
    "Understanding your business",
    "Planning website pages",
    "Creating CRM modules",
    "Designing mobile-first sections",
    "Preparing your live preview"
];

function inferIndustry(prompt = "") {
    const text = prompt.toLowerCase();

    if (text.includes("fitness") || text.includes("gym") || text.includes("trainer")) return "Fitness";
    if (text.includes("academy") || text.includes("school") || text.includes("course")) return "Education";
    if (text.includes("real estate") || text.includes("property") || text.includes("portal")) return "Real Estate";
    if (text.includes("clinic") || text.includes("doctor") || text.includes("health")) return "Healthcare";
    if (text.includes("restaurant") || text.includes("cafe")) return "Restaurant";
    if (text.includes("store") || text.includes("shop") || text.includes("ecommerce")) return "Online Store";
    if (text.includes("law") || text.includes("legal")) return "Legal";
    if (text.includes("beauty") || text.includes("salon")) return "Beauty";
    if (text.includes("saas") || text.includes("software") || text.includes("app")) return "SaaS";

    return "";
}

function normalizeIndustry(value) {
    if (value === "Online Store") return "Ecommerce / Store";
    if (value === "SaaS") return "SaaS / Tech";
    return value || "Business";
}

function buildBusinessName(description) {
    const clean = String(description || "").trim();

    if (!clean) return "";

    return clean
        .replace(/^we\s+need\s+to\s+build\s+/i, "")
        .replace(/^build\s+/i, "")
        .replace(/^create\s+/i, "")
        .replace(/^a\s+/i, "")
        .split(" ")
        .slice(0, 3)
        .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
        .join(" ");
}

function defaultCtaForIndustry(industry) {
    if (industry === "Fitness") return "Book Trial";
    if (industry === "Real Estate") return "Contact Us";
    if (industry === "Online Store") return "Buy Online";
    if (industry === "Education") return "Join Program";
    return "Contact Us";
}

function buildPayload(form) {
    const industry = normalizeIndustry(form.industry);

    return {
        businessName: form.businessName || buildBusinessName(form.description) || "My Business",
        industry,
        subIndustry: industry,
        description: form.description || "A professional business website generated by Shriansh Nexus.",
        goalOfWebsite: form.callToActionType || "Generate Leads",
        callToActionType: form.callToActionType || "Contact Us",
        brandStyle: form.brandStyle || "Premium",
        tone: "Professional",
        primaryAudience: "Local customers",
        pages: ["home", "about", "services", "contact"],
        offeredItems: []
    };
}

export default function MobileGeneratePage() {
    const query = useMemo(() => new URLSearchParams(window.location.search), []);
    const initialPrompt = query.get("prompt") || "";
    const detectedIndustry = inferIndustry(initialPrompt);

    const [step, setStep] = useState(initialPrompt.trim() ? 1 : 0);
    const [screen, setScreen] = useState("setup");
    const [loadingIndex, setLoadingIndex] = useState(0);
    const [isTyping, setIsTyping] = useState(true);

    const [form, setForm] = useState({
        description: initialPrompt,
        businessName: buildBusinessName(initialPrompt),
        industry: detectedIndustry,
        callToActionType: defaultCtaForIndustry(detectedIndustry),
        brandStyle: "Premium"
    });

    const [previewSessionId, setPreviewSessionId] = useState("");
    const [saving, setSaving] = useState(false);

    const progress =
        screen === "generating"
            ? 88
            : screen === "preview"
                ? 100
                : step === 0
                    ? 20
                    : step === 1
                        ? 55
                        : 78;

    useEffect(() => {
        if (screen !== "setup") return;

        setIsTyping(true);
        const timer = setTimeout(() => setIsTyping(false), 420);

        return () => clearTimeout(timer);
    }, [screen, step]);

    useEffect(() => {
        if (screen !== "generating") return;

        const timer = setInterval(() => {
            setLoadingIndex((prev) => (prev + 1) % loadingSteps.length);
        }, 1350);

        return () => clearInterval(timer);
    }, [screen]);

    const update = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const next = () => {
        if (step === 0) {
            if (!form.description.trim()) {
                toast.error("Describe your business first.");
                return;
            }

            const industry = inferIndustry(form.description);
            setForm((prev) => ({
                ...prev,
                industry: prev.industry || industry,
                businessName: prev.businessName || buildBusinessName(prev.description),
                callToActionType: prev.callToActionType || defaultCtaForIndustry(industry)
            }));

            setStep(1);
            return;
        }

        if (step === 1) {
            if (!form.businessName.trim()) {
                toast.error("Enter your business name.");
                return;
            }

            if (!form.industry) {
                toast.error("Choose business type.");
                return;
            }

            setStep(2);
            return;
        }

        if (step === 2) {
            if (!form.callToActionType) {
                toast.error("Choose the main action.");
                return;
            }

            generate();
        }
    };

    const back = () => {
        if (screen === "preview") {
            setScreen("setup");
            setStep(2);
            return;
        }

        if (step > 0) {
            setStep((prev) => prev - 1);
            return;
        }

        window.history.back();
    };

    const generate = async () => {
        try {
            setScreen("generating");

            const response = await createAndQueuePreviewSession(buildPayload(form));
            const id = response?.previewSessionId;

            if (!id) throw new Error("Preview session not created.");

            setPreviewSessionId(id);

            const interval = setInterval(async () => {
                const status = await getPreviewSessionStatus(id);

                if (status?.status === "Generated") {
                    clearInterval(interval);
                    await getPreviewSession(id);
                    setScreen("preview");
                    toast.success("Preview ready.");
                }

                if (status?.status === "Failed") {
                    clearInterval(interval);
                    setScreen("setup");
                    setStep(2);
                    toast.error("Generation failed. Please try again.");
                }
            }, 2800);
        } catch (error) {
            console.error(error);
            setScreen("setup");
            setStep(2);
            toast.error("Failed to start preview.");
        }
    };

    const saveToCrm = async () => {
        if (!previewSessionId) return;

        try {
            setSaving(true);

            const me = await getCurrentUser().catch(() => null);
            if (!me) {
                redirectToSignup(window.location.href);
                return;
            }

            const result = await claimPreviewSession(previewSessionId);

            if (result?.projectId) {
                window.location.href = `https://admin.shrianshnexus.in/crm/projects/${result.projectId}`;
                return;
            }

            toast.error("Could not save project.");
        } catch (error) {
            console.error(error);
            toast.error("Could not save preview.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <main className="mgv-page">
            <header className="mgv-header">
                <button type="button" onClick={back}>
                    <ArrowLeft size={19} />
                </button>

                <div className="mgv-brand">
                    <strong>Nexus</strong>
                    <span>AI website builder</span>
                </div>

                <div className="mgv-ai-pill">AI</div>
            </header>

            <div className="mgv-progress-track">
                <span style={{ width: `${progress}%` }} />
            </div>

            {screen === "setup" && (
                <>
                    <section className="mgv-chat">
                        <div className="mgv-ai-message">
                            <div className="mgv-ai-icon">
                                <Sparkles size={16} />
                            </div>

                            <div className="mgv-ai-copy">
                                <span>AI Assistant</span>

                                {isTyping ? (
                                    <div className="mgv-typing">
                                        <i />
                                        <i />
                                        <i />
                                    </div>
                                ) : (
                                    <>
                                        {step === 0 && (
                                            <>
                                                <h1>Tell me what you want to build.</h1>
                                                <p>Describe your idea in one sentence.</p>

                                                <div className="mgv-form-card">
                                                    <label>
                                                        Your idea
                                                        <textarea
                                                            value={form.description}
                                                            onChange={(e) => update("description", e.target.value)}
                                                            placeholder="e.g. Real estate portal for property listings and leads"
                                                        />
                                                    </label>
                                                </div>
                                            </>
                                        )}

                                        {step === 1 && (
                                            <>
                                                <h1>Confirm your business setup.</h1>
                                                <p>
                                                    I detected your business type. You can adjust it before generating.
                                                </p>

                                                <div className="mgv-form-card">
                                                    <label>
                                                        Business name
                                                        <input
                                                            value={form.businessName}
                                                            onChange={(e) => update("businessName", e.target.value)}
                                                            placeholder="e.g. Prime Estate Portal"
                                                        />
                                                    </label>

                                                    <label>
                                                        Business type
                                                        <select
                                                            value={form.industry}
                                                            onChange={(e) => {
                                                                const industry = e.target.value;
                                                                update("industry", industry);
                                                                update("callToActionType", defaultCtaForIndustry(industry));
                                                            }}
                                                        >
                                                            <option value="">Choose business type</option>
                                                            {industryOptions.map((option) => (
                                                                <option key={option} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </label>

                                                    <label>
                                                        Description
                                                        <textarea
                                                            value={form.description}
                                                            onChange={(e) => update("description", e.target.value)}
                                                            placeholder="Describe your business..."
                                                        />
                                                    </label>
                                                </div>
                                            </>
                                        )}

                                        {step === 2 && (
                                            <>
                                                <h1>Choose goal and style.</h1>
                                                <p>This helps Nexus prepare the first website preview.</p>

                                                <div className="mgv-section-label">Main action</div>
                                                <div className="mgv-chip-grid">
                                                    {ctaOptions.map((option) => (
                                                        <button
                                                            key={option}
                                                            type="button"
                                                            className={form.callToActionType === option ? "active" : ""}
                                                            onClick={() => update("callToActionType", option)}
                                                        >
                                                            {option}
                                                        </button>
                                                    ))}
                                                </div>

                                                <div className="mgv-section-label">Website style</div>
                                                <div className="mgv-chip-grid">
                                                    {styleOptions.map((option) => (
                                                        <button
                                                            key={option}
                                                            type="button"
                                                            className={form.brandStyle === option ? "active" : ""}
                                                            onClick={() => update("brandStyle", option)}
                                                        >
                                                            {option}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </section>

                    <div className="mgv-inline-actions">
                        <button type="button" className="ghost" onClick={back}>
                            Back
                        </button>

                        <button type="button" onClick={next} disabled={isTyping}>
                            {step === 2 ? "Generate" : "Next"}
                            <ArrowRight size={17} />
                        </button>
                    </div>
                </>
            )}

            {screen === "generating" && (
                <section className="mgv-generating">
                    <div className="mgv-live-card">
                        <div className="mgv-live-top">
                            <div>
                                <span>Live preview</span>
                                <strong>{buildPayload(form).businessName}</strong>
                            </div>

                            <Loader2 className="mgv-spin" size={20} />
                        </div>

                        <div className="mgv-live-browser">
                            <div className="mgv-live-hero" />
                            <div className="mgv-live-blocks">
                                <span />
                                <span />
                                <span />
                            </div>
                            <div className="mgv-live-lines">
                                <span />
                                <span />
                                <span />
                            </div>
                        </div>
                    </div>

                    <h1>Generating your website</h1>
                    <p>{loadingSteps[loadingIndex]}</p>
                </section>
            )}

            {screen === "preview" && (
                <section className="mgv-preview">
                    <div className="mgv-success">
                        <CheckCircle2 size={26} />
                        <h1>Your preview is ready</h1>
                        <p>Review the website and save it to your CRM workspace.</p>
                    </div>

                    <div className="mgv-preview-frame">
                        <iframe
                            title="Website preview"
                            src={`${API_BASE_URL}/api/website/preview-session/${previewSessionId}/document?r=${Date.now()}`}
                        />
                    </div>

                    <div className="mgv-preview-actions">
                        <button type="button" onClick={saveToCrm} disabled={saving}>
                            {saving ? "Saving..." : "Save & Open CRM"}
                        </button>

                        <button
                            type="button"
                            className="ghost"
                            onClick={() => redirectToLogin(window.location.href)}
                        >
                            Login
                        </button>
                    </div>
                </section>
            )}
        </main>
    );
}